import { useAuthStore } from '../store/authStore'
import type { ApiResponse, ExamType, ExamSummary, ExamFramework } from '@enghustle/shared'

const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`

class APIClient {
  private getHeaders(): HeadersInit {
    const token = useAuthStore.getState().token
    return {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  async get<T>(path: string): Promise<T> {
    const makeReq = () => fetch(`${BASE}${path}`, { headers: this.getHeaders() })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    const makeReq = () => fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    const makeReq = () => fetch(`${BASE}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const makeReq = () => fetch(`${BASE}${path}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  async delete<T>(path: string): Promise<T> {
    const makeReq = () => fetch(`${BASE}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  async postFormData<T>(path: string, formData: FormData): Promise<T> {
    const token = useAuthStore.getState().token
    const makeReq = () => fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    })
    const res = await makeReq()
    return this.handle<T>(res, makeReq)
  }

  private async handle<T>(res: Response, originalRequest?: () => Promise<Response>): Promise<T> {
    // Attempt token refresh on 401 before giving up
    if (res.status === 401 && originalRequest) {
      const refreshed = await this.tryRefresh()
      if (refreshed) {
        // Retry original request with new token
        const retryRes = await originalRequest()
        const retryData: ApiResponse<T> = await retryRes.json()
        if (!retryData.success) throw new Error(retryData.error ?? 'Request failed')
        return retryData.data
      } else {
        useAuthStore.getState().clearAuth()
        throw new Error('Session expired. Please log in again.')
      }
    }

    if (res.status === 401) {
      useAuthStore.getState().clearAuth()
      throw new Error('Session expired. Please log in again.')
    }

    const data: ApiResponse<T> = await res.json()
    if (!data.success) throw new Error(data.error ?? 'Request failed')
    return data.data
  }

  private async tryRefresh(): Promise<boolean> {
    const { refreshToken, setAuth } = useAuthStore.getState()
    if (!refreshToken) return false

    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      if (!res.ok) return false
      const data = await res.json()
      if (data.success && data.data.token) {
        setAuth(data.data.token, data.data.refreshToken ?? refreshToken)
        return true
      }
    } catch {}
    return false
  }
}

export const api = new APIClient()

// ─── Typed API helpers ────────────────────────────────────────────────────────

type AuthResponse = { token: string; refreshToken: string; userId: string; role: string }

export const authAPI = {
  login: (email: string, password: string) =>
    api.post<AuthResponse>('/auth/login', { email, password }),
  register: (email: string, password: string, name: string) =>
    api.post<AuthResponse>('/auth/register', { email, password, name }),
  refresh: (refreshToken: string) =>
    api.post<{ token: string; refreshToken: string; role: string }>('/auth/refresh', { refreshToken }),
  googleLogin: (credential: string) =>
    api.post<AuthResponse>('/auth/google', { credential }),
  facebookLogin: (accessToken: string) =>
    api.post<AuthResponse>('/auth/facebook', { accessToken }),
}

export const sessionAPI = {
  start: (availableMinutes: number) =>
    api.post<any>('/sessions/start', { availableMinutes }),
  complete: (sessionId: string) =>
    api.post<{ xp: number; newMilestones: string[] }>(`/sessions/${sessionId}/complete`),
  today: () =>
    api.get<{ completed: boolean }>('/sessions/today'),
  calendar: (days = 14) =>
    api.get<Array<{ date: string; status: 'completed' | 'missed' | 'today' | 'future'; skillFocus: string | null; xpEarned: number }>>(`/sessions/calendar?days=${days}`),
  difficulty: () =>
    api.get<{ successRate: number; currentDifficultyLevel: number; recommendedDifficultyLevel: number; adjustment: number; zone: 'flow' | 'too_hard' | 'too_easy' }>('/sessions/difficulty'),
  // Today's adaptive program (FR-003/FR-006) — login / dashboard preview.
  program: (availableMinutes = 15) =>
    api.get<any>(`/sessions/program?availableMinutes=${availableMinutes}`),
}

export const writingAPI = {
  submit: (body: {
    taskType: 'task1' | 'task2'
    taskPrompt: string
    text: string
    targetLevel: string
    sessionId: string
    examType?: string
  }) => api.post<any>('/writing/submit', body),
  requestRewrite: (submissionId: string) =>
    api.post<{ rewrite: string }>(`/writing/${submissionId}/rewrite`),
  history: (page = 1) =>
    api.get<any[]>(`/writing/history?page=${page}`),
  exportPdf: async (submissionId: string) => {
    const token = useAuthStore.getState().token
    const res = await fetch(`${BASE}/writing/${submissionId}/export-pdf`, {
      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '1' },
    })
    if (!res.ok) throw new Error('Export failed')
    const html = await res.text()
    // Open in new tab for browser print-to-PDF
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  },
}

export const speakingAPI = {
  submit: (formData: FormData) =>
    api.postFormData<any>('/speaking/submit', formData),
  history: (page = 1) =>
    api.get<any[]>(`/speaking/history?page=${page}`),
  // LME-3 Shadowing
  shadowSet: () => api.get<{ enabled: boolean; source?: string; title?: string; sentences?: string[] }>('/speaking/shadow'),
  shadowScore: (formData: FormData) =>
    api.postFormData<{ available: boolean; pronunciationScore: number | null; fluencyScore: number | null; provider: string }>('/speaking/shadow', formData),
}

export const vocabAPI = {
  due: () => api.get<any[]>('/vocab/due'),
  all: () => api.get<any[]>('/vocab/all'),
  review: (wordId: string, quality: number) =>
    api.post('/vocab/review', { wordId, quality }),
  save: (word: string, contextSentence: string, sourceContentId: string) =>
    api.post('/vocab/save', { word, contextSentence, sourceContentId }),
  dashboard: () => api.get<any>('/vocab/dashboard'),
  // LME-1/2: learning-method flags, active-recall grading, context examples
  methods: () => api.get<{ activeRecall: boolean; contextExamples: boolean; fsrs: boolean }>('/vocab/methods'),
  examples: (wordId: string) =>
    api.get<{ definition: string | null; examples: string[] }>(`/vocab/examples/${encodeURIComponent(wordId)}`),
  reviewRecall: (wordId: string, answer: string) =>
    api.post<any>('/vocab/review/recall', { wordId, answer }),
  define: (word: string) => api.get<any>(`/vocab/define?word=${encodeURIComponent(word)}`),
  exportAnki: async () => {
    const token = useAuthStore.getState().token
    const res = await fetch(`${BASE}/vocab/export/anki`, {
      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '1' },
    })
    if (!res.ok) throw new Error('Export failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `enghustle-vocab-${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    URL.revokeObjectURL(url)
  },
}

export const progressAPI = {
  dashboard: () => api.get<any>('/progress/dashboard'),
  streak: () => api.get<any>('/progress/streak'),
  xp: () => api.get<any>('/progress/xp'),
  milestones: () => api.get<any[]>('/progress/milestones'),
  freezeStreak: () => api.post<{ streakSaved: boolean; freezeCount: number }>('/progress/streak/freeze'),
  plan: () => api.get<any>('/progress/plan'),
  rebalancePlan: () => api.post<{ dailyStudyMinutes: number; increased: number }>('/progress/plan/rebalance'),
  momentum: () => api.get<any>('/progress/momentum'),
}

export const practiceAPI = {
  getQuestions: (skill: string, part?: string, examType?: string) =>
    api.get<any>(`/practice/questions?skill=${skill}${part ? `&part=${part}` : ''}${examType ? `&examType=${examType}` : ''}`),
  // Targeted micro-drill (FR-006): pass the user's weak error types to focus on.
  getDrill: (skill: string, errorTypes: string[], part?: string, examType?: string) =>
    api.get<any>(`/practice/questions?skill=${skill}&errorType=${encodeURIComponent(errorTypes.join(','))}${part ? `&part=${part}` : ''}${examType ? `&examType=${examType}` : ''}`),
  // Per-part writing/speaking prompt (FR-029). Returns { source, prompt, modelAnswer, figure? }.
  getPrompt: (skill: 'writing' | 'speaking', part?: string, examType?: string) =>
    api.get<{ source: 'db' | 'empty'; prompt?: string; modelAnswer?: string | null; figure?: import('@enghustle/shared').WritingFigure }>(
      `/practice/prompt?skill=${skill}${part ? `&part=${part}` : ''}${examType ? `&examType=${examType}` : ''}`,
    ),
  submitAnswer: (questionId: string, answer: string, timeSpentSeconds: number, sessionId?: string) =>
    api.post<{ isCorrect: boolean | null; correctAnswer: string; explanation: string }>(
      '/practice/answer',
      { questionId, answer, timeSpentSeconds, sessionId },
    ),
}

export const challengeAPI = {
  getCurrent: () => api.get<any>('/challenges/current'),
  join: () => api.post<any>('/challenges/join'),
  syncProgress: () => api.post<any>('/challenges/sync-progress'),
  getLeaderboard: () => api.get<any>('/challenges/leaderboard'),
  getBuddy: () => api.get<any>('/challenges/buddy'),
  requestBuddy: () => api.post<any>('/challenges/buddy/request'),
}

export const adminAPI = {
  // FR-016 — CMS
  getAIConfig: () => api.get<any>('/admin/ai-config'),
  getAWSCredit: () => api.get<any>('/admin/ai-config/aws-credit'),
  listContent: (params?: { skill?: string; page?: number }) =>
    api.get<any[]>(`/admin/content?${new URLSearchParams(Object.entries({ skill: params?.skill ?? '', page: String(params?.page ?? 1) }).filter(([, v]) => v && v !== '')).toString()}`),
  createContent: (body: any) => api.post<any>('/admin/content', body),
  deleteContent: (id: string) => api.delete<any>(`/admin/content/${id}`),
  listQuestions: (params?: { skill?: string; page?: number }) =>
    api.get<any[]>(`/admin/questions?${new URLSearchParams(Object.entries({ skill: params?.skill ?? '', page: String(params?.page ?? 1) }).filter(([, v]) => v && v !== '')).toString()}`),
  createQuestion: (body: any) => api.post<any>('/admin/questions', body),
  deleteQuestion: (id: string) => api.delete<any>(`/admin/questions/${id}`),
  getContentStats: () => api.get<any>('/admin/content/stats'),

  // FR-020 — User Management
  listUsers: (params?: { search?: string; subscription?: string; page?: number }) =>
    api.get<any[]>(`/admin/users?${new URLSearchParams(Object.entries({ search: params?.search ?? '', subscription: params?.subscription ?? '', page: String(params?.page ?? 1) }).filter(([, v]) => v && v !== '')).toString()}`),
  getUserDetail: (userId: string) => api.get<any>(`/admin/users/${userId}`),
  updateUser: (userId: string, body: any) => api.patch<any>(`/admin/users/${userId}`, body),
  lockUser: (userId: string, reason: string) => api.post<any>(`/admin/users/${userId}/lock`, { reason }),
  unlockUser: (userId: string) => api.post<any>(`/admin/users/${userId}/unlock`, {}),
  deleteUser: (userId: string) => api.delete<any>(`/admin/users/${userId}`),
  exportUsersCSV: async () => {
    const token = useAuthStore.getState().token
    const res = await fetch(`${BASE}/admin/users/export`, {
      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '1' },
    })
    if (!res.ok) throw new Error('Export failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'users.csv'
    a.click()
    URL.revokeObjectURL(url)
  },
  getInactiveUsers: (days: number) => api.get<any[]>(`/admin/users/inactive?days=${days}`),
  getAuditLog: () => api.get<any[]>('/admin/users/audit-log'),

  // FR-023 — System Monitor
  getSystemHealth: () => api.get<any>('/admin/system/health'),
  getAICosts: (days: number) => api.get<any[]>(`/admin/system/ai-costs?days=${days}`),
  getAILogs: (params: { module?: string; provider?: string; page?: number }) =>
    api.get<any[]>(`/admin/system/ai-logs?${new URLSearchParams(Object.entries({ module: params.module ?? '', provider: params.provider ?? '', page: String(params.page ?? 1) }).filter(([, v]) => v && v !== '')).toString()}`),
  getRuntimeErrors: () => api.get<any[]>('/admin/system/errors'),
  resolveError: (id: string) => api.patch<any>(`/admin/system/errors/${id}/resolve`, {}),

  // FR-024 — System Config
  getFeatureFlags: () => api.get<any[]>('/admin/config/flags'),
  createFeatureFlag: (flag: string, description?: string) => api.post<any>('/admin/config/flags', { flag, description }),
  updateFeatureFlag: (flag: string, body: { isEnabled?: boolean; rolloutPercent?: number }) =>
    api.patch<any>(`/admin/config/flags/${encodeURIComponent(flag)}`, body),
  deleteFeatureFlag: (flag: string) => api.delete<any>(`/admin/config/flags/${encodeURIComponent(flag)}`),
  getMaintenance: () => api.get<any>('/admin/config/maintenance'),
  setMaintenance: (enabled: boolean, message?: string) => api.patch<any>('/admin/config/maintenance', { enabled, message }),
  getFreeLimits: () => api.get<any>('/admin/config/limits'),
  updateFreeLimits: (limits: Record<string, number>) => api.patch<any>('/admin/config/limits', limits),
  getSecurityConfig: () => api.get<any>('/admin/config/security'),
  getTeacherWhitelist: () => api.get<any[]>('/admin/config/teachers'),

  // FR-019 — AI Content Generator
  startContentGeneration: (body: any) => api.post<any>('/admin/generator/generate', body),
  getContentGenJob: (jobId: string) => api.get<any>(`/admin/generator/jobs/${jobId}`),
  approveAllItems: (jobId: string) => api.post<any>(`/admin/generator/jobs/${jobId}/approve-all`, {}),
  approveItem: (itemId: string) => api.post<any>(`/admin/generator/items/${itemId}/approve`, {}),
  rejectItem: (itemId: string) => api.post<any>(`/admin/generator/items/${itemId}/reject`, {}),
  regenerateItem: (itemId: string) => api.post<any>(`/admin/generator/items/${itemId}/regenerate`, {}),
  getContentGenHistory: () => api.get<any[]>('/admin/generator/history'),

  // FR-021 — Revenue & Subscriptions
  getRevenueSummary: (days?: number) => api.get<any>(`/admin/revenue/summary${days ? `?days=${days}` : ''}`),
  getTransactions: (params?: { status?: string; page?: number }) =>
    api.get<any[]>(`/admin/revenue/transactions?${new URLSearchParams(Object.entries({ status: params?.status ?? '', page: String(params?.page ?? 1) }).filter(([, v]) => v && v !== '')).toString()}`),
  refundTransaction: (id: string, reason: string) => api.post<any>(`/admin/revenue/transactions/${id}/refund`, { reason }),
  getCoupons: () => api.get<any[]>('/admin/revenue/coupons'),
  createCoupon: (body: any) => api.post<any>('/admin/revenue/coupons', body),
  toggleCoupon: (code: string) => api.patch<any>(`/admin/revenue/coupons/${encodeURIComponent(code)}/toggle`, {}),
  deleteCoupon: (code: string) => api.delete<any>(`/admin/revenue/coupons/${encodeURIComponent(code)}`),
  exportTransactionsCSV: async () => {
    const token = useAuthStore.getState().token
    const res = await fetch(`${BASE}/admin/revenue/export`, {
      headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '1' },
    })
    if (!res.ok) throw new Error('Export failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'transactions.csv'; a.click()
    URL.revokeObjectURL(url)
  },

  // FR-022 — Notifications & Banners
  getBroadcasts: (page?: number) => api.get<any[]>(`/admin/notifications/broadcasts${page ? `?page=${page}` : ''}`),
  sendBroadcast: (body: any) => api.post<any>('/admin/notifications/broadcasts', body),
  getBanners: () => api.get<any[]>('/admin/notifications/banners'),
  createBanner: (body: any) => api.post<any>('/admin/notifications/banners', body),
  toggleBanner: (id: string) => api.patch<any>(`/admin/notifications/banners/${id}/toggle`, {}),
  deleteBanner: (id: string) => api.delete<any>(`/admin/notifications/banners/${id}`),

  // FR-025 — Analytics
  getAnalyticsOverview: (days?: number) => api.get<any>(`/admin/analytics/overview${days ? `?days=${days}` : ''}`),
  getDailyMetrics: (days?: number) => api.get<any[]>(`/admin/analytics/daily${days ? `?days=${days}` : ''}`),
  getRetentionCohorts: () => api.get<any[]>('/admin/analytics/retention'),
  getOnboardingFunnel: () => api.get<any[]>('/admin/analytics/funnel'),
  getSkillUsage: () => api.get<any[]>('/admin/analytics/skills'),
  getTopUsers: (limit?: number) => api.get<any[]>(`/admin/analytics/top-users${limit ? `?limit=${limit}` : ''}`),

  // FR-029 — Multi-Exam Framework registry
  listExams: () => api.get<any[]>('/admin/exams'),
  getExamParts: (examType: string) => api.get<any[]>(`/admin/exams/${examType}/parts`),
  upsertExam: (examType: string, body: any) => api.put<any>(`/admin/exams/${examType}`, body),
  toggleExam: (examType: string, isEnabled: boolean) =>
    api.patch<any>(`/admin/exams/${examType}/enabled`, { isEnabled }),
  updateExamScoreMapping: (examType: string, scoreMapping: Record<string, string>) =>
    api.put<any>(`/admin/exams/${examType}/score-mapping`, { scoreMapping }),
  upsertExamPart: (examType: string, body: any) => api.put<any>(`/admin/exams/${examType}/parts`, body),
  deleteExamPart: (examType: string, skill: string, partCode: string) =>
    api.delete<any>(`/admin/exams/${examType}/parts/${skill}/${partCode}`),
}

export const userAPI = {
  me: () => api.get<any>('/users/me'),
  updateProfile: (body: {
    name?: string
    examType?: ExamType
    targetLevel?: string
    dailyStudyMinutes?: number
    examDate?: string | null
    motivation?: string | null
    notifDailyReminder?: boolean
    notifWeeklyReport?: boolean
  }) => api.put<any>('/users/me/profile', body),
  updateLanguage: (language: 'vi' | 'en') =>
    api.patch<any>('/users/language', { language }),
  markLanguageSuggestionShown: () =>
    api.patch<any>('/users/language/suggestion-shown', {}),
  // FR-029 — switch target exam (keeps CEFR/θ ability)
  updateExamType: (examType: ExamType) =>
    api.patch<{ examType: ExamType }>('/users/me/exam-type', { examType }),
}

export type NotificationItem = {
  id: string
  type: string
  title: string
  body: string | null
  icon: string | null
  link: string | null
  isRead: boolean
  createdAt: string
}

export const notificationAPI = {
  list: (page = 1) => api.get<NotificationItem[]>(`/notifications?page=${page}`),
  unreadCount: () => api.get<{ count: number }>('/notifications/unread-count'),
  markRead: (id: string) => api.patch<any>(`/notifications/${id}/read`, {}),
  markAllRead: () => api.patch<any>('/notifications/read-all', {}),
}

// FR-029 — public exam framework registry
export const examAPI = {
  list: () => api.get<ExamSummary[]>('/exams'),
  getFramework: (examType: ExamType) =>
    api.get<ExamFramework>(`/exams/${examType}/framework`),
}

export const teacherAPI = {
  getClasses: () => api.get<any[]>('/teacher/classes'),
  createClass: (body: { name: string; description?: string }) =>
    api.post<{ id: string; inviteCode: string }>('/teacher/classes', body),
  getStudents: (classId: string) => api.get<any[]>(`/teacher/classes/${classId}/students`),
  joinClass: (inviteCode: string) =>
    api.post<{ joined: boolean; className: string; alreadyMember?: boolean }>('/teacher/classes/join', { inviteCode }),
  getMyClasses: () => api.get<any[]>('/teacher/my-classes'),
  createAssignment: (body: any) => api.post<any>('/teacher/assignments', body),
  getAssignments: (classId: string) => api.get<any[]>(`/teacher/assignments?classId=${classId}`),
  getSubmissions: (type: 'writing' | 'speaking', classId?: string) =>
    api.get<any[]>(`/teacher/submissions?type=${type}${classId ? `&classId=${classId}` : ''}`),
  addComment: (body: { submissionType: 'writing' | 'speaking'; submissionId: string; studentId: string; comment: string; scoreOverride?: number }) =>
    api.post<any>('/teacher/comments', body),
  getComments: (submissionType: string, submissionId: string) =>
    api.get<any[]>(`/teacher/comments/${submissionType}/${submissionId}`),
  deleteComment: (id: string) => api.delete<any>(`/teacher/comments/${id}`),
}

export const chatAPI = {
  getMessages: (since?: string) =>
    api.get<any>(`/chat/buddy/messages${since ? `?since=${encodeURIComponent(since)}` : ''}`),
  sendMessage: (content: string) =>
    api.post<any>('/chat/buddy/messages', { content }),
  markRead: () => api.post<any>('/chat/buddy/messages/read'),
  getUnread: () => api.get<any>('/chat/buddy/unread'),
}

export const discussionAPI = {
  getCurrent: () => api.get<any>('/discussion/current'),
  createPost: (content: string, parentId?: string) =>
    api.post<any>('/discussion/posts', { content, parentId }),
  likePost: (postId: string) => api.post<any>(`/discussion/posts/${postId}/like`),
  deletePost: (postId: string) => api.delete<any>(`/discussion/posts/${postId}`),
}

export const mockTestAPI = {
  start: () => api.post<any>('/mock-test/start'),
  getQuestions: (testId: string, skill: string) =>
    api.get<any>(`/mock-test/${testId}/questions?skill=${skill}`),
  submitAnswer: (testId: string, body: any) =>
    api.post<any>(`/mock-test/${testId}/answer`, body),
  complete: (testId: string) => api.post<any>(`/mock-test/${testId}/complete`),
  history: () => api.get<any[]>('/mock-test/history'),
  report: (testId: string) => api.get<any>(`/mock-test/${testId}/report`),
}
